import { useMemo, useState } from 'react'
import { api } from '../api.js'
import { Banner, Button, Card, ErrorState, Field, Loading, Metric, MetricRow, StatusPill, Table } from '../components.jsx'
import { useApi } from '../hooks.js'
import PageHeader from './PageHeader.jsx'

/**
 * AMC contractors and external maintenance firms, native to the console.
 *
 * A firm has to exist here before a vendor login can be created for it — the user form's Firm dropdown reads
 * this list, and the server rejects a VENDOR account with no `vendorId`. That ordering was an
 * order-dependency the D1 work surfaced, so the two pages live side by side rather than in separate tabs
 * with separate logins.
 */
export default function Vendors() {
  const list = useApi('/admin/vendors')
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)

  const all = Array.isArray(list.data) ? list.data : []
  const rows = useMemo(() => {
    if (!q.trim()) return all
    const t = q.trim().toLowerCase()
    return all.filter((v) => [v.name, v.amcRef, v.email, v.phone, v.siteEngineer]
      .some((x) => x && String(x).toLowerCase().includes(t)))
  }, [all, q])

  async function run(fn, text) {
    setBusy(true); setNotice(null)
    try {
      await fn()
      list.reload()
      setNotice({ kind: 'success', text })
      setEditing(null)
    } catch (e) {
      setNotice({ kind: 'danger', text: e.message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        title="Vendors"
        subtitle="AMC contractors and external firms. A firm must exist before a vendor login can be created."
        actions={<Button onClick={() => setEditing('new')}>New firm</Button>}
      />

      {notice && <Banner kind={notice.kind}>{notice.text}</Banner>}
      {list.loading && <Loading rows={5} />}
      {list.error && <ErrorState error={list.error} onRetry={list.reload} />}

      {list.data && (
        <>
          <MetricRow>
            <Metric label="Firms" value={all.length} />
            <Metric label="Active" value={all.filter((v) => v.active !== false).length} kind="success" />
            <Metric
              label="Inactive"
              value={all.filter((v) => v.active === false).length}
              hint="Cannot be assigned work"
            />
            <Metric label="With AMC ref" value={all.filter((v) => v.amcRef).length} />
          </MetricRow>

          <Card
            actions={
              <input
                aria-label="Search firms"
                placeholder="Name, AMC ref, contact…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                style={{ minWidth: 220 }}
              />
            }
          >
            <Table
              keyOf={(v) => v.id}
              columns={[
                { key: 'name', header: 'Firm', render: (v) => <strong>{v.name}</strong> },
                { key: 'amcRef', header: 'AMC ref', render: (v) => v.amcRef || <span className="muted">—</span> },
                {
                  key: 'contact',
                  header: 'Contact',
                  render: (v) => (
                    <span className="cell-stack">
                      {v.phone && <span>{v.phone}</span>}
                      {v.email && <span className="muted">{v.email}</span>}
                      {!v.phone && !v.email && <span className="muted">—</span>}
                    </span>
                  ),
                },
                { key: 'engineer', header: 'Site engineer', render: (v) => v.siteEngineer || <span className="muted">—</span> },
                {
                  key: 'state',
                  header: 'State',
                  render: (v) => (v.active === false
                    ? <StatusPill status="CLOSED" title="Inactive — cannot be assigned work" />
                    : <StatusPill status="ACTIVE" />),
                },
                { key: 'act', header: '', render: (v) => <Button variant="secondary" onClick={() => setEditing(v)}>Edit</Button> },
              ]}
              rows={rows}
              empty={<p className="muted">No firms registered. Add one before creating a vendor login.</p>}
            />
          </Card>
        </>
      )}

      {editing && (
        <VendorForm
          vendor={editing === 'new' ? null : editing}
          busy={busy}
          onClose={() => setEditing(null)}
          onSubmit={(body) => {
            const isNew = editing === 'new'
            run(
              () => api(isNew ? '/admin/vendors' : `/admin/vendors/${editing.id}`, {
                method: isNew ? 'POST' : 'PATCH',
                body,
              }),
              isNew ? `Registered ${body.name}.` : `${body.name} updated.`,
            )
          }}
          onDelete={() => run(
            () => api(`/admin/vendors/${editing.id}`, { method: 'DELETE' }),
            `${editing.name} deactivated.`,
          )}
        />
      )}
    </>
  )
}

function VendorForm({ vendor, busy, onClose, onSubmit, onDelete }) {
  const isNew = !vendor
  const [f, setF] = useState({
    name: vendor?.name || '',
    amcRef: vendor?.amcRef || '',
    phone: vendor?.phone || '',
    email: vendor?.email || '',
    siteEngineer: vendor?.siteEngineer || '',
    notes: vendor?.notes || '',
  })
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <form
        className="sheet"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit({
            name: f.name.trim(),
            amcRef: f.amcRef.trim() || null,
            phone: f.phone.trim() || null,
            email: f.email.trim() || null,
            siteEngineer: f.siteEngineer.trim() || null,
            notes: f.notes.trim() || null,
          })
        }}
      >
        <header className="sheet-head">
          <h2>{isNew ? 'New firm' : `Edit ${vendor.name}`}</h2>
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </header>

        <div className="sheet-body">
          <div className="grid-2">
            <Field label="Firm name" id="vn">
              <input id="vn" value={f.name} onChange={set('name')} required autoFocus />
            </Field>
            <Field label="AMC reference" id="va" hint="Contract number, if there is one">
              <input id="va" value={f.amcRef} onChange={set('amcRef')} />
            </Field>
            <Field label="Phone" id="vp">
              <input id="vp" value={f.phone} onChange={set('phone')} inputMode="tel" />
            </Field>
            <Field label="Email" id="ve">
              <input id="ve" type="email" value={f.email} onChange={set('email')} />
            </Field>
            <Field label="Site engineer" id="vs" hint="Their representative at the plant">
              <input id="vs" value={f.siteEngineer} onChange={set('siteEngineer')} />
            </Field>
          </div>
          <Field label="Notes" id="vnote">
            <textarea id="vnote" value={f.notes} onChange={set('notes')} rows={3} />
          </Field>

          {!isNew && vendor.active !== false && (
            <>
              <p className="muted">
                Deactivating keeps the firm and its job history. It stops appearing as an assignment option,
                and the D1 work made that guard singular about which firm it names.
              </p>
              <Button type="button" variant="danger" disabled={busy} onClick={onDelete}>
                Deactivate {vendor.name}
              </Button>
            </>
          )}
        </div>

        <footer className="sheet-foot">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy || !f.name.trim()}>
            {busy ? 'Saving…' : isNew ? 'Register firm' : 'Save changes'}
          </Button>
        </footer>
      </form>
    </div>
  )
}
